import axiosInstance, { endpoints } from 'src/utils/axios';

import { createArrayHook, createResourceActions } from './utils/create-action';

// ----------------------------------------------------------------------

const endpoint = endpoints.c.stores;

// Custom actions specific to stores
const customStoreActions = {
  /**
   * Get additional store information
   * @param {number} id - Store ID
   * @param {Object} args - Fetch arguments
   */
  getStoreInfo: (id, args) => {
    const url = `${endpoint}/${id}/info`;
    return createArrayHook(url)(args);
  },

  /**
   * Get store dataset history
   * @param {number} id - Store ID
   * @param {Object} args - Fetch arguments
   */
  getDatasetHistory: (id, args) => {
    const url = `${endpoint}/${id}/dataset-history`;
    return createArrayHook(url)(args);
  },

  /**
   * Get store number history
   * @param {number} id - Store ID
   * @param {Object} args - Fetch arguments
   */
  getNumberHistory: (id, args) => {
    const url = `${endpoint}/${id}/number-history`;
    return createArrayHook(url)(args);
  },

  /**
   * Get store scorecard data
   * @param {number} id - Store ID
   * @param {Object} args - Fetch arguments
   */
  getScorecard: (id, args) => {
    const url = `${endpoint}/${id}/scorecard`;
    return createArrayHook(url)(args);
  },

  /**
   * Assign stores to organization
   * @param {string} orgType - Organization type code
   * @param {number} orgValue - Organization ID
   * @param {Array<number>} storesId - Array of store IDs
   */
  assignToOrg: (orgType, orgValue, storesId) => {
    const url = `${endpoint}/assign`;
    return axiosInstance.post(url, { orgType, orgValue, storesId });
  },

  /**
   * Bulk update store status
   * @param {Array<number>} ids - Array of store IDs
   * @param {boolean} active - Active status
   */
  bulkUpdateStatus: (ids, active) => {
    const url = `${endpoint}/bulk-update-status`;
    return axiosInstance.post(url, { ids, active });
  },

  /**
   * Custom upsert
   */
  upsert: (store) => {
    const url = `${endpoint}/upsert`;
    return axiosInstance.post(url, store);
  }
};

// Create standardized actions with custom ones
const {
  list: useGetStores,
  count: useCountStores,
  get: useGetStore,
  create: createStore,
  update: updateStore,
  delete: deleteStore,
  upsert: upsertStore,
  bulkDelete: bulkDeleteStores,
  ...customActions
} = createResourceActions(endpoint, {
  customActions: customStoreActions
});

// Export individual actions
export {
  createStore,
  deleteStore,
  updateStore,
  upsertStore,
  useGetStore,
  useGetStores,
  useCountStores,
  bulkDeleteStores,
};

// Export custom actions with better names
export const useGetStoreInfo = customActions.getStoreInfo;
export const useGetStoreDatasetHistory = customActions.getDatasetHistory;
export const useGetStoreNumbersHistory = customActions.getNumberHistory;
export const useGetStoreScorecard = customActions.getScorecard;
export const onAssignStoreToOrg = customActions.assignToOrg;
export const bulkUpdateStoreStatus = customActions.bulkUpdateStatus;
